import React, { useRef, useState, createContext, useMemo, useContext } from 'react';
import { useRecoilValue, useSetRecoilState } from 'recoil';
import { blockInfoGraphicType, isReportState, recoilBlockContentSize } from 'venn-state';
import Measure from 'react-measure';
import { isChartType } from 'venn-utils';
import type { InfoGraphicTypeEnum } from 'venn-api';

interface BlockMeasureProps {
  id: string;
}

export interface BlockMeasureContextValue {
  height: number | undefined;
}

/** for blocks like Effective Duration, Credit Quality Breakdown, Maturity Breakdown
 *  need to use wrapper's height from here, but width from inner ag-grid for content overflow indicator
 *  */
export const BlockMeasureContext = createContext<BlockMeasureContextValue>({ height: undefined });
BlockMeasureContext.displayName = 'BlockMeasureContext';

export const useBlockMeasureHeight = (): number | undefined => {
  const ctx = useContext(BlockMeasureContext);
  return ctx.height;
};

export const BlockMeasure: React.FC<React.PropsWithChildren<BlockMeasureProps>> = ({ id, children }) => {
  const isStudio = !useRecoilValue(isReportState);
  const setBlockContentSize = useSetRecoilState(recoilBlockContentSize.transformedState(id));
  const contentRef = useRef<HTMLDivElement | null>(null);

  const [height, setHeight] = useState<number | undefined>(undefined);
  const contextValue = useMemo(() => ({ height }), [height]);

  const onResize = () => {
    if (contentRef.current) {
      const { offsetWidth, offsetHeight } = contentRef.current;
      if (infoGraphicType === 'GRID') {
        setHeight((prev) => (prev !== offsetHeight ? offsetHeight : prev));
      } else if (infoGraphicType !== 'PIE') {
        // PIE self-reports its natural (overflow-aware) content size from PieChart;
        // measuring the clipped wrapper here would mask horizontal overflow.
        setBlockContentSize({ width: offsetWidth, height: offsetHeight });
      }
    }
  };

  const infoGraphicType: InfoGraphicTypeEnum | undefined = useRecoilValue(blockInfoGraphicType(id));
  const isChartBlock = infoGraphicType ? isChartType(infoGraphicType) : false;

  const contentClassName = [
    infoGraphicType !== 'GRID' &&
    infoGraphicType !== 'HEATMAP' &&
    infoGraphicType !== 'BOX_CHART' &&
    infoGraphicType !== 'DIVERGENT_BAR'
      ? 'w-full h-full'
      : '',
    infoGraphicType === 'LINE' ? '[&>div:first-child]:w-full' : '',
    infoGraphicType === 'PIE' ? 'min-w-[100px] min-h-[100px]' : isChartBlock ? 'min-w-[150px] min-h-[140px]' : '',

    isChartBlock && infoGraphicType !== 'SCATTER' && infoGraphicType !== 'BOX_CHART' && infoGraphicType !== 'BAR'
      ? 'flex'
      : 'block',
  ]
    .filter(Boolean)
    .join(' ');

  if (isStudio || infoGraphicType === 'CORRELATION' || infoGraphicType === 'PEER_GROUP') {
    return children;
  }

  return (
    <BlockMeasureContext.Provider value={contextValue}>
      <Measure bounds onResize={onResize}>
        {({ measureRef }) => (
          <div id="measureWrapper" ref={measureRef}>
            <div id="contentWrapper" className={contentClassName} ref={contentRef}>
              {children}
            </div>
          </div>
        )}
      </Measure>
    </BlockMeasureContext.Provider>
  );
};
